import {
  pexIconRegistry,
  type PexIconName,
  type PexVendorIconProps,
} from './pex-icon-registry'
import {
  PEX_ICON_SIZES,
  type PexIconSize,
  type PexIconTone,
  type PexIconWeight,
} from './pex-icon-types'

const toneClasses: Record<PexIconTone, string> = {
  default: 'text-[var(--icon-default)]',
  muted: 'text-[var(--color-text-muted)]',
  strong: 'text-[var(--icon-strong)]',
  interactive: 'text-[var(--icon-interactive)]',
  navigation: 'text-[var(--sidebar-icon)]',
  'navigation-active': 'text-[var(--sidebar-active-icon)]',
  signal: 'text-[var(--color-signal)]',
  success: 'text-[var(--color-success)]',
  warning: 'text-[var(--color-warning)]',
  danger: 'text-[var(--color-danger)]',
  info: 'text-[var(--color-info)]',
  'on-emphasis': 'text-[var(--color-action-foreground)]',
  topbar: 'text-[var(--topbar-fg)]',
  // Takes the surrounding text colour (currentColor). For icons inside a
  // button, badge or link whose colour, hover and dark-mode states are set
  // by the parent or by the icon's own className.
  inherit: '',
}

type AccessibilityProps =
  | {
      decorative?: true
      label?: never
    }
  | {
      decorative: false
      label: string
    }

type BaseProps = Omit<
  PexVendorIconProps,
  'aria-hidden' | 'aria-label' | 'children' | 'size' | 'weight'
> & {
  name: PexIconName
  size?: PexIconSize
  weight?: PexIconWeight
  tone?: PexIconTone
}

export type PexIconProps = BaseProps & AccessibilityProps

/**
 * Product-wide Phosphor adapter.
 *
 * Defaults match the approved navigation language: Regular / 20px / muted.
 * Bold is reserved for status emphasis, Duotone for empty-state or
 * onboarding illustrations, and Fill for a toggle that is on (for example a
 * favourited heart).
 */
export function PexIcon({
  name,
  size = 'md',
  weight = 'regular',
  tone = 'default',
  decorative = true,
  label,
  className = '',
  ...svgProps
}: PexIconProps) {
  const Icon = pexIconRegistry[name]

  return (
    <Icon
      {...svgProps}
      data-pex-icon={name}
      size={PEX_ICON_SIZES[size]}
      weight={weight}
      className={`shrink-0 ${toneClasses[tone]} ${className}`.replace(/\s+/g, ' ').trim()}
      aria-hidden={decorative ? true : undefined}
      aria-label={decorative ? undefined : label}
      role={decorative ? undefined : 'img'}
    />
  )
}
